<script>
import {
	authComputed,
	configComputed,
	configMethods,
	reportsComputed,
	reportsMethods
} from '@/store/helpers'
import {
	reportTypes,
	runSheetClients,
	runSheetLocales,
	runSheetLocations
} from '@/store/modules/reports'

export default {
	name: 'ReportFilter',
	i18nOptions: {
		keyPrefix: 'PORTAL.Reports'
	},
	watch: {
		reportType() {
			if (!this.localeList.some(({ id }) => id === this.localeId)) {
				this.localeId = ''
			}
			if (!this.locationList.some(({ id }) => id === this.locationId)) {
				this.locationId = ''
			}
			if (!this.clientList.some(({ id }) => id === this.clientId)) {
				this.clientId = ''
			}
		}
	},
	data() {
		return {
			clientId: '',
			reportType: reportTypes[0],
			pickupDate: '',
			locationId: '',
			localeId: ''
		}
	},
	computed: {
		...authComputed,
		...configComputed,
		...reportsComputed,
		hasClientDropdown() {
			return (
				this.isAdmin ||
				(this.currentUser &&
					this.currentUser.roles.includes('CLT') &&
					this.clients.length > 1)
			)
		},
		canGenerate() {
			if (this.hasClientDropdown) {
				return Boolean(
					this.clientId &&
						this.reportType &&
						this.pickupDate &&
						this.localeId &&
						this.locationId
				)
			} else {
				return Boolean(
					this.currentClient &&
						this.currentClient.id &&
						this.reportType &&
						this.pickupDate &&
						this.localeId &&
						this.locationId
				)
			}
		},
		locationList() {
			if (this.reportType === 'RUN_SHEET') {
				return this.locations.filter(({ id }) =>
					runSheetLocations.includes(id)
				)
			}
			return this.locations
		},
		localeList() {
			if (this.reportType === 'RUN_SHEET') {
				return this.locales.filter(({ id }) =>
					runSheetLocales.includes(id)
				)
			}
			return this.locales
		},
		clientList() {
			if (this.reportType === 'RUN_SHEET') {
				return this.clients.filter(({ id }) =>
					runSheetClients.includes(id)
				)
			}
			return this.clients
		},
		reportTypeList() {
			return this.isAdmin ||
				(this.currentUser &&
					this.currentUser.roles.includes('CLT') &&
					this.clients.some(({ id }) => runSheetClients.includes(id)))
				? this.reportTypes
				: this.reportTypes.filter(
						reportType => reportType !== 'RUN_SHEET'
				  )
		}
	},
	methods: {
		...configMethods,
		...reportsMethods,
		dateDisabled(ymd, date) {
			const now = new Date()
			const priorDate = new Date(new Date().setDate(now.getDate() - 60))
			return date > now || date < priorDate
		},
		onGenerate() {
			if (!this.canGenerate) {
				return
			}
			const {
				clientId,
				reportType: type,
				pickupDate,
				currentClient,
				locationId,
				localeId
			} = this

			const reportClientId = this.hasClientDropdown
				? clientId
				: currentClient
				? currentClient.id
				: ''

			if (this.reportType === 'POD') {
				this.generatePodReport({
					filter: {
						pickupDate,
						clientId: reportClientId,
						type,
						locationId,
						locale: localeId
					}
				})
			} else if (this.reportType === 'RUN_SHEET') {
				this.generateRunSheetReport({
					filter: {
						date: pickupDate,
						locationId,
						locale: localeId,
						clientId: reportClientId
					}
				})
			} else {
				this.generateReport({
					filter: {
						pickupDate,
						clientId: reportClientId,
						type,
						locationId,
						locale: localeId
					},
					onFailed: () => this.$bvModal.show('failed-modal')
				})
			}
		}
	}
}
</script>

<template>
	<div class="card">
		<div class="card-body">
			<h4 class="header-title mb-3">{{ $t('filter_heading') }}</h4>
			<div class="d-flex flex-wrap">
				<div>
					<div class="font-weight-bold mb-1">
						{{ $t('filter_report_type_label') }}
					</div>
					<b-form-select v-model="reportType">
						<b-form-select-option :value="''">
							{{ $t('filter_report_type_placeholder') }}
						</b-form-select-option>
						<b-form-select-option
							v-for="currentReportType of reportTypeList"
							:key="currentReportType"
							:value="currentReportType"
						>
							{{ $t(`report_type.${currentReportType}`) }}
						</b-form-select-option>
					</b-form-select>
				</div>
				<div>
					<div class="font-weight-bold mb-1">
						{{ $t('filter_report_date_label') }}
					</div>
					<b-input-group class="datepickerContainer mb-2">
						<b-input-group-append>
							<b-form-datepicker
								v-model="pickupDate"
								:date-disabled-fn="dateDisabled"
								button-only
							/>
						</b-input-group-append>
						<b-form-input
							v-model="pickupDate"
							type="text"
							:placeholder="$t('filter_report_date_placeholder')"
							autocomplete="off"
						/>
					</b-input-group>
				</div>
				<div>
					<div class="font-weight-bold mb-1">
						{{ $t('filter_location_label') }}
					</div>
					<b-form-select v-model="locationId">
						<b-form-select-option :value="''">
							{{ $t('filter_location_placeholder') }}
						</b-form-select-option>
						<b-form-select-option
							v-for="location of locationList"
							:key="location.id"
							:value="location.id"
						>
							{{ location.id }}
						</b-form-select-option>
					</b-form-select>
				</div>
				<div>
					<div class="font-weight-bold mb-1">
						{{ $t('filter_locale_label') }}
					</div>
					<b-form-select v-model="localeId">
						<b-form-select-option :value="''">
							{{ $t('filter_locale_placeholder') }}
						</b-form-select-option>
						<b-form-select-option
							v-for="locale of localeList"
							:key="locale.id"
							:value="locale.id"
						>
							{{ locale.id }}
						</b-form-select-option>
					</b-form-select>
				</div>
				<div v-if="hasClientDropdown">
					<div class="font-weight-bold mb-1">
						{{ $t('filter_client_label') }}
					</div>
					<b-form-select v-model="clientId">
						<b-form-select-option :value="''">
							{{ $t('filter_client_placeholder') }}
						</b-form-select-option>
						<b-form-select-option
							v-for="currentClient of clientList"
							:key="currentClient.id"
							:value="currentClient.id"
						>
							{{ currentClient.id }}
						</b-form-select-option>
					</b-form-select>
				</div>
				<button
					:class="[
						'btn',
						'btn-primary',
						'mt-2',
						'align-self-center',
						canGenerate ? '' : 'disabled'
					]"
					@click="onGenerate"
				>
					{{ $t('filter_button_generate') }}
				</button>
			</div>
			<b-toast
				v-model="showErrorToast"
				@hidden="hideErrorToast"
				id="my-toast"
				solid
				toaster="b-toaster-top-center"
			>
				<template #toast-title>
					<div class="d-flex flex-grow-1 align-items-baseline">
						<strong class="mr-auto">{{ toastTitle }}</strong>
					</div>
				</template>
				{{ toastMessage }}
			</b-toast>
			<b-modal id="failed-modal" :title="$t('failed_modal_title')">
				{{ $t('failed_modal_message') }}
			</b-modal>
		</div>
	</div>
</template>

<style scoped lang="scss">
.d-flex > :not(:last-child) {
	margin-right: 1rem;
}

button.disabled {
	cursor: not-allowed;
}

.custom-select,
.datepickerContainer input {
	background-color: #eef2f7;
	color: #313a46;
}

.datepickerContainer {
	input {
		border-left: none;
		padding: 0.45rem 0.2rem;
	}
	&::v-deep .dropdown-toggle {
		border-top-left-radius: 0.25rem !important;
		border-bottom-left-radius: 0.25rem !important;
		padding: 0.45rem 0.5rem;
		background-color: #eef2f7;
		color: #6c757d;
		border: 1px solid #dee2e6;
		border-right: none;
		box-shadow: none;
	}
}
</style>
